import { useEffect, useState } from "react";
import dynamic from 'next/dynamic';
// next
import { useRouter } from 'next/router';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  Card,
} from "@mui/material";;
// hooks
import useAuth from "../../hooks/useAuth";
import useResponsive from "../../hooks/useResponsive";
// components
import Page from "../../components/Page";
import Image from "../../components/Image";
import DIOText from "../../components/DIOText";
import SvgIconStyle from "../../components/SvgIconStyle";
import Iconify from "../../components/Iconify";
import {
  ScoutTabs,
  FreeToProDialog,
  ScoutCrewCard,
} from "./";
import {
  FliersDetailDrawer,
  FliersDetailDialog,
} from '../../sections/fliers';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
// lottie
const DotLottiePlayer = dynamic(
  () => import('@dotlottie/react-player').then(mod => mod.DotLottiePlayer),
  { ssr: false }
);
import '@dotlottie/react-player/dist/index.css';

// ----------------------------------------------------------------------

export default function FreePage() {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const companyId = user?.employee?.company?.[0].id;
  const [openPlanUpgradeDialog, setOpenPlanUpgradeDialog] = useState(false);

  const [isOpenFliersDetailDrawer, setIsOpenFliersDetailDrawer] = useState(false);
  const [isOpenFliersDetailDialog, setIsOpenFliersDetailDialog] = useState(false);
  const [selectedCoffeeChat, setSelectedCoffeeChat] = useState(null);

  const router = useRouter();
  const { query } = router;

  const { data: scouts, isLoading, mutate: mutateScout } = useSWR(companyId ? `/api/company/${companyId}/scout/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { data: fliersCoffeeChats, mutate: mutateCoffeeChat } = useSWR(companyId ? `/api/company/${companyId}/fliers/coffee/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const selectCrew = (crew) => {
    if (crew) {
      setSelectedCoffeeChat({ crew: { id: crew.id }, coffee_chat_status: "suggestable", proxy: crew.scout_proxy_at });
      fliersCoffeeChats?.find((chat) => {
        if (chat?.crew?.id === crew?.id) {
          setSelectedCoffeeChat(chat);
        }
      });
      if (smUp) {
        setIsOpenFliersDetailDrawer(true);
      } else {
        setIsOpenFliersDetailDialog(true);
      }
    }
  }

  useEffect(() => {
    if (query?.profile && !isLoading) {
      scouts?.find((crew) => {
        if (parseInt(query?.profile) === crew?.id) {
          selectCrew(crew);
        }
      });
    }
  }, [query, isLoading]);

  return (
    <Page title="Scout" sx={{ pb: { xs: 4, sm : 6 } }}>
      <Box sx={{ px: { xs: 3, sm: 5 }, mx: "auto", pb: 1 }} maxWidth="lg">
        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1 }}>
          <Image src={"/icons/scout-logo.svg"} sx={{ width: smUp ? "94px" : "82.25px", height: smUp ? "16px" : "14px", color: "primary.main" }} />
          <Typography variant="caption" sx={{ lineHeight: "12.8px" }}>
            커피챗을 제안해보세요!
          </Typography>
        </Stack>
        <ScoutTabs tab="recommend" />
      </Box>
      {isLoading && (
      <Box sx={{ px: { xs: 3, md: 5 }, mx: "auto" }} maxWidth="lg">
        <Box sx={{ width: 1 }}>
          <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
      )}
      {!isLoading && scouts?.length === 0 && (
      <Box sx={{ px: { xs: 3, md: 5 }, mx: "auto" }} maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: 1,
            height: smUp ? 'calc(100vh - 246px)' : 'calc(100vh - 204px)',
            minHeight: "400px",
            alignItems: "center", justifyContent: "center", textAlign: "center", bgcolor: "grey.200", borderRadius: 2, padding: 3 
          }}
        >
          <Typography variant="h4" gutterBottom>
            요청하신 인재를 찾고 있어요.
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            {smUp && '적합한 '}인재를 찾으면 카카오톡으로 알려드릴게요.
          </Typography>
          <DotLottiePlayer
            autoplay
            loop
            mode="normal"
            src={'/lottie/scout-finding.lottie'}
            style={{ width: smUp ? 350 : 250, height: smUp ? 200 : 150, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          />
        </Box>
      </Box>
      )}
      {!isLoading && scouts?.length > 0 && (
      <Box sx={{ px: { xs: 3, md: 5 }, mx: "auto" }} maxWidth="lg">
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: "grey.200",
          }}
        >
          <Stack direction={{ sm: "row", xs: "column" }} justifyContent={{ sm: "space-between", xs: "flex-start" }} spacing={2} alignItems={{ sm: "center", xs: "flex-start"}}>
            <Stack direction="row" spacing={1} alignItems="center">
              {smUp && (
                <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'text.secondary' }} />
              )}
              <Typography variant="body2">
                <b>PRO 플랜으로 업그레이드</b> 하고,{!smUp && <br />} 더 많은 인재를 만나보세요.
              </Typography>
            </Stack>
            <Button variant="contained" onClick={() => setOpenPlanUpgradeDialog(true)}>
              PRO 플랜 알아보기
            </Button>
          </Stack>
        </Box>

        {/* crew cards */}
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {scouts.map((scout, index) => (
            <ScoutCrewCard profile={scout} key={index} selectProfile={selectCrew} />
          ))}
          {scouts.length < 3 && (
            <SearchingCard />
          )}
          {scouts.length < 2 && (
            <SearchingCard />
          )}
        </Box>

        <Box sx={{ my: { xs: 5, sm: 8 }, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="h6" gutterBottom>
            더 많은 인재와 컨택하고 싶으신가요?
          </Typography>
          <Typography variant="body2" paragraph>
            <DIOText />의 검증된 경력자 인재 풀을 인재 영입에 활용해보세요.
          </Typography>
          <Button variant="contained" size="large" onClick={() => setOpenPlanUpgradeDialog(true)}>
            플랜 업그레이드
          </Button>
        </Box>
      </Box>
      )}
      <FreeToProDialog open={openPlanUpgradeDialog} closeDialog={() => setOpenPlanUpgradeDialog(false)} buttonSource="page" />
      <FliersDetailDrawer
        isOpen={isOpenFliersDetailDrawer}
        closeDrawer={() => setIsOpenFliersDetailDrawer(false)}
        coffeeChat={selectedCoffeeChat}
        reload={mutateCoffeeChat}
        reloadScout={mutateScout}
      />
      <FliersDetailDialog
        isOpen={isOpenFliersDetailDialog}
        closeDialog={() => { setIsOpenFliersDetailDialog(false) }}
        coffeeChat={selectedCoffeeChat}
        reload={mutateCoffeeChat}
        reloadScout={mutateScout}
      />
    </Page>
  )
}

function SearchingCard () {
  return (
    <Card sx={{ minHeight: "220px", boxShadow: "none", border: "1px solid", borderColor: (theme) => theme.palette.grey[500_24] }}>
      <Box
        sx={{
          width: 1,
          justifyContent: "center",
          justifyItems: "center",
          alignItems: "center",
          position: "absolute",
          p:0, m:0, left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        }}
      >
        <Stack direction="row" sx={{ width: 1, justifyContent: "center" }}>
          <SvgIconStyle src="/icons/ic_search.svg" sx={{ width: 24, height: 24, color: "text.disabled" }} />
          <Typography variant="subtitle1" sx={{ color: "text.disabled" }}>
            인재 서치 중
          </Typography>
        </Stack>
      </Box>
    </Card>
  )
}